import React, { useEffect, useRef } from 'react';
import { Animated, StyleSheet, Easing } from 'react-native';
import Svg, { Circle, Defs, LinearGradient, Stop } from 'react-native-svg';

import theme from '@themes/theme';

/**
 * Rotating ring spinner (TA-10068). Two use cases, same component:
 * - Full-screen/large: omit `color` to keep the default brand gradient
 *   (e.g. payment-processing in `CreateSubscription`).
 * - Inline/button: pass a small `size` (~16-24) and a solid `color`
 *   matching the surface it sits on (e.g. white on a black "Log in"
 *   button, or the surface's text color for an outlined button) — see
 *   `stories/atoms/Spinner.stories.tsx`'s "Inline button" story.
 *
 * Per the global spinner rules (TA-10068 ticket): when used inline in a
 * button, the caller is responsible for replacing the button's label
 * with this spinner and locking the surrounding form while it's shown
 * — this component only renders the spinner itself.
 */
interface SpinnerProps {
  size?: number;
  strokeWidth?: number;
  duration?: number;
  color?: string;
  testID?: string;
}

const Spinner: React.FC<SpinnerProps> = ({
  size = 180,
  strokeWidth = 14,
  duration = 1000,
  color,
  testID = 'spinner',
}) => {
  // Unique per mount so multiple inline Spinners on the same page don't
  // collide on a shared SVG <defs> id.
  const gradientId = `spinner-gradient-${React.useId()}`;
  const spinValue = useRef(new Animated.Value(0)).current;

  useEffect(() => {
    let isActive = true;
    const spin = () => {
      spinValue.setValue(0);
      Animated.timing(spinValue, {
        toValue: 1,
        duration,
        easing: Easing.linear,
        useNativeDriver: true,
      }).start(({ finished }) => {
        if (finished && isActive) spin();
      });
    };

    spin();

    return () => {
      isActive = false;
      spinValue.stopAnimation();
    };
  }, [spinValue, duration]);

  const rotate = spinValue.interpolate({
    inputRange: [0, 1],
    outputRange: ['0deg', '360deg'],
    extrapolate: 'extend',
  });

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDasharray = circumference * 0.75;

  return (
    <Animated.View
      testID={testID}
      style={[
        styles.container,
        {
          width: size,
          height: size,
          transform: [{ rotate }],
        },
      ]}>
      <Svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        {!color && (
          <Defs>
            <LinearGradient id={gradientId} x1='0%' y1='0%' x2='100%' y2='100%'>
              {/* No theme token matches these two mint highlight shades;
                  the final stop reuses the existing greenSecondary token. */}
              <Stop offset='0%' stopColor='#A7F3D0' stopOpacity='0.3' />
              <Stop offset='50%' stopColor='#6EE7B7' stopOpacity='0.3' />
              <Stop
                offset='100%'
                stopColor={theme.colors.greenSecondary}
                stopOpacity='1'
              />
            </LinearGradient>
          </Defs>
        )}
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={color || `url(#${gradientId})`}
          strokeWidth={strokeWidth}
          strokeLinecap='round'
          strokeDasharray={`${strokeDasharray} ${circumference}`}
          fill='transparent'
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </Svg>
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export { Spinner };
